/**
 * Language configuration for the pre-transcription picker.
 *
 * Single source of truth for which languages the UI offers, grouped into tiers.
 * Every `code` here MUST be accepted by the backend's SUPPORTED_LANGUAGES set
 * (api/transcribe.ts) — enforced by languages.test.ts so the UI can never offer
 * a code the backend would 400 on.
 *
 * Tiers:
 *  - recommended   : tuned for South African accents + Afrikaans/English code-switching (AssemblyAI Universal-2)
 *  - supported      : global languages (small files: prompted Gemini, GEMINI_TRANSCRIBE_MODEL; large files: AssemblyAI → Deepgram)
 *  - sa-indigenous  : SA languages transcribed by Gemini (BETA, best-effort — these are
 *                     low-resource; a verbatim "do not translate" hint is sent server-side).
 *                     Gated on services.gemini === 'configured' from GET /api/health.
 *                     (A future Lelapa Vulavula path can upgrade quality once LELAPA_API_KEY lands.)
 *
 * Decision: docs/specs/2026-06-24-language-picker-expansion.md (DTP-005 c-balanced).
 */

export type LanguageTier = 'recommended' | 'supported' | 'sa-indigenous';

export interface LanguageOption {
  code: string;
  label: string;
  flag: string;
  tier: LanguageTier;
  /** Short helper line shown under the label. */
  note?: string;
  /** Capability that /api/health must report 'configured' before this language is selectable. */
  requiresCapability?: 'lelapa' | 'gemini';
}

export const LANGUAGES: LanguageOption[] = [
  // ── Recommended — tuned for South African English + Afrikaans (incl. code-switching) ──
  { code: 'en', label: 'English',   flag: '🇬🇧', tier: 'recommended' },
  { code: 'af', label: 'Afrikaans', flag: '🇿🇦', tier: 'recommended', note: 'Enables Dutch correction pass' },

  // ── Supported — global languages (small files: prompted Gemini; large files: AssemblyAI → Deepgram) ──
  { code: 'fr', label: 'French',     flag: '🇫🇷', tier: 'supported' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸', tier: 'supported' },
  { code: 'de', label: 'German',     flag: '🇩🇪', tier: 'supported' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹', tier: 'supported' },
  { code: 'nl', label: 'Dutch',      flag: '🇳🇱', tier: 'supported' },
  { code: 'it', label: 'Italian',    flag: '🇮🇹', tier: 'supported' },
  { code: 'hi', label: 'Hindi',      flag: '🇮🇳', tier: 'supported' },
  { code: 'ja', label: 'Japanese',   flag: '🇯🇵', tier: 'supported' },
  { code: 'ko', label: 'Korean',     flag: '🇰🇷', tier: 'supported' },
  { code: 'zh', label: 'Chinese',    flag: '🇨🇳', tier: 'supported' },
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦', tier: 'supported' },

  // ── South African languages — transcribed by Gemini (BETA, gated on services.gemini) ──
  { code: 'zu', label: 'isiZulu',   flag: '🇿🇦', tier: 'sa-indigenous', requiresCapability: 'gemini', note: 'Beta' },
  { code: 'xh', label: 'isiXhosa',  flag: '🇿🇦', tier: 'sa-indigenous', requiresCapability: 'gemini', note: 'Beta' },
  { code: 'st', label: 'Sesotho',   flag: '🇿🇦', tier: 'sa-indigenous', requiresCapability: 'gemini', note: 'Beta' },
  { code: 'tn', label: 'Setswana',  flag: '🇿🇦', tier: 'sa-indigenous', requiresCapability: 'gemini', note: 'Beta' },
  { code: 've', label: 'Tshivenda', flag: '🇿🇦', tier: 'sa-indigenous', requiresCapability: 'gemini', note: 'Beta' },
];

/**
 * Mirror of api/transcribe.ts → SUPPORTED_LANGUAGES (minus the 'auto' sentinel).
 * Kept in sync by languages.test.ts: every LANGUAGES code must be in this set.
 */
export const BACKEND_SUPPORTED_LANGUAGES = [
  'en', 'af',
  'zu', 'xh', 'st', 'tn', 've',
  'ar', 'zh', 'fr', 'de', 'ja', 'es',
  'pt', 'hi', 'ko', 'nl', 'it',
] as const;

export const DEFAULT_LANGUAGE = 'en';

/** Runtime capability flags, derived from GET /api/health → services. */
export interface Capabilities {
  lelapa?: boolean;
  gemini?: boolean;
}

export function languagesByTier(tier: LanguageTier): LanguageOption[] {
  return LANGUAGES.filter((l) => l.tier === tier);
}

/**
 * Whether a language is selectable given the live capability flags.
 * Languages with no `requiresCapability` are always enabled; capability-gated
 * ones (Lelapa SA-indigenous) are enabled only when that capability is true.
 */
export function isLanguageEnabled(lang: LanguageOption, caps: Capabilities): boolean {
  if (!lang.requiresCapability) return true;
  return caps[lang.requiresCapability] === true;
}
